import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import Notiflix from "notiflix";
import Modal from "components/common/Modal";
import TransactionFailureModal from "components/common/TransactionFailureModal";
import TransactionSuccessModal from "components/common/TransactionSuccessModal";
import ShareModal from "components/profile/ShareModal";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  CircularProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import { useHistory } from "react-router-dom";

import translate from "i18n/translate";

const GeneralPayModal = ({
  number,
  selectedBill,
  isDisabled,
  clearSelections,
  totalAmount,
  apiCall,
  title = "Pay",
  onSubmitSuccess,
  showEditAmount = false,
  extraFields = [],
  identity,
  provider = "jawwal",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [transactionData, setTransactionData] = useState(null);
  const [savedBillData, setSavedBillData] = useState({ bills: [], amount: 0 }); // Save bill data for printing
  const [countryCode, setCountryCode] = useState("970");
  const [wNumber, setWNumber] = useState(number);
  const [email, setEmail] = useState(null);
  const [changeText, setChangeText] = useState(totalAmount);
  const [isEditable, setIsEditable] = useState(false);
  const [isMultiBillLoading, setIsMultiBillLoading] = useState(false);
  const history = useHistory();

  useEffect(() => {
    setChangeText(totalAmount);
  }, [totalAmount]);

  const handleCountryCodeChange = (event) => {
    setCountryCode(event.target.value);
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setTransactionData(null);
    setSavedBillData({ bills: [], amount: 0 });
    // Use replace instead of push to prevent white screen on back navigation
    setTimeout(() => {
      history.replace("/pay_bills");
    }, 100);
  };

  const handleFailureModalClose = () => {
    setShowFailureModal(false);
    setFailureMessage("");
    // Clear selections and reset state to prevent white screen issues
    clearSelections();
    setWNumber(number);
    setEmail(null);
    setChangeText(0);
    setIsEditable(false);
  };

  const handleShareClick = () => {
    setShowShareModal(true);
  };

  const [showPrintPreview, setShowPrintPreview] = useState(false);

  const handlePrintClick = () => {
    if (!transactionData) return;
    setShowPrintPreview(true);
  };

  const handleConfirmPrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(createModernReceiptHTML());
    printWindow.document.close();
    printWindow.print();
    setShowPrintPreview(false);
  };

  const createModernReceiptHTML = () => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    // Use saved bill data for printing
    const actualTotalAmount = savedBillData?.amount > 0
      ? savedBillData.amount.toFixed(2)
      : '0.00';
    const billsToDisplay = savedBillData?.bills || [];

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Jawwal Bill Payment Receipt</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
            
            * { margin: 0; padding: 0; box-sizing: border-box; }
            
            body { 
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              padding: 20px;
              min-height: 100vh;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            
            .receipt-container {
              background: white;
              border-radius: 20px;
              box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
              padding: 0;
              max-width: 400px;
              width: 100%;
              overflow: hidden;
              position: relative;
            }
            
            .receipt-header {
              background: linear-gradient(135deg, #FF6B35 0%, #F7931E 100%);
              color: white;
              padding: 30px 25px;
              text-align: center;
              position: relative;
            }
            
            .jawwal-logo {
              width: 80px;
              height: 80px;
              background: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 15px;
              padding: 10px;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            }
            
            .jawwal-logo img {
              width: 100%;
              height: 100%;
              object-fit: contain;
              border-radius: 50%;
            }
            
            .receipt-title {
              font-size: 24px;
              font-weight: 700;
              margin-bottom: 8px;
              text-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            
            .receipt-subtitle {
              font-size: 14px;
              opacity: 0.9;
              font-weight: 400;
            }
            
            .receipt-body {
              padding: 30px 25px;
            }
            
            .status-badge {
              display: inline-block;
              background: #4CAF50;
              color: white;
              padding: 8px 16px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: 25px;
            }
            
            .info-grid {
              display: grid;
              gap: 20px;
              margin-bottom: 25px;
            }
            
            .info-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 15px 0;
              border-bottom: 1px solid #f0f0f0;
            }
            
            .info-item:last-child {
              border-bottom: none;
            }
            
            .info-label {
              font-size: 14px;
              color: #666;
              font-weight: 500;
            }
            
            .info-value {
              font-size: 14px;
              color: #333;
              font-weight: 600;
              text-align: right;
            }
            
            .amount-highlight {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              border-radius: 15px;
              padding: 20px;
              text-align: center;
              margin: 25px 0;
            }
            
            .amount-label {
              font-size: 12px;
              opacity: 0.8;
              margin-bottom: 5px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            
            .amount-value {
              font-size: 28px;
              font-weight: 700;
            }
            
            .bill-details {
              background: #f8f9fa;
              border-radius: 15px;
              padding: 20px;
              margin: 20px 0;
            }
            
            .bill-details-title {
              font-size: 16px;
              font-weight: 600;
              color: #333;
              margin-bottom: 15px;
              text-align: center;
            }
            
            .bill-item {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 10px 0;
              border-bottom: 1px solid #e0e0e0;
            }
            
            .bill-item:last-child {
              border-bottom: none;
            }
            
            .bill-number {
              font-size: 14px;
              color: #666;
              font-weight: 500;
            }
            
            .bill-amount {
              font-size: 14px;
              color: #333;
              font-weight: 600;
            }
            
            .receipt-footer {
              background: #f8f9fa;
              padding: 20px 25px;
              text-align: center;
              border-top: 1px solid #e0e0e0;
            }
            
            .footer-text {
              font-size: 12px;
              color: #666;
              line-height: 1.5;
            }
            
            .transaction-id {
              background: #e3f2fd;
              color: #1976d2;
              padding: 8px 12px;
              border-radius: 8px;
              font-family: 'Courier New', monospace;
              font-size: 11px;
              font-weight: 600;
              letter-spacing: 0.5px;
            }
            
            .decorative-line {
              height: 4px;
              background: linear-gradient(90deg, #FF6B35, #F7931E, #FF6B35);
              margin: 20px 0;
              border-radius: 2px;
            }
            
            @media print {
              @page {
                size: A4;
                margin: 10mm;
              }
              
              body { 
                background: white; 
                padding: 0; 
                min-height: auto;
                margin: 0;
              }
              
              .receipt-container { 
                box-shadow: none; 
                border: 1px solid #ddd; 
                max-width: 100%;
                page-break-inside: avoid;
                margin: 0;
              }
              
              .receipt-header {
                padding: 15px 20px;
              }
              
              .jawwal-logo {
                width: 50px;
                height: 50px;
                margin: 0 auto 8px;
                padding: 5px;
              }
              
              .receipt-title {
                font-size: 18px;
                margin-bottom: 4px;
              }
              
              .receipt-subtitle {
                font-size: 12px;
              }
              
              .receipt-body {
                padding: 15px 20px;
              }
              
              .status-badge {
                padding: 4px 12px;
                font-size: 10px;
                margin-bottom: 12px;
              }
              
              .info-grid {
                gap: 8px;
                margin-bottom: 12px;
              }
              
              .info-item {
                padding: 8px 0;
              }
              
              .info-label,
              .info-value {
                font-size: 11px;
              }
              
              .decorative-line {
                height: 2px;
                margin: 10px 0;
              }
              
              .amount-highlight {
                padding: 12px;
                margin: 12px 0;
              }
              
              .amount-label {
                font-size: 10px;
                margin-bottom: 3px;
              }
              
              .amount-value {
                font-size: 20px;
              }
              
              .bill-details {
                padding: 12px;
                margin: 12px 0;
              }
              
              .bill-details-title {
                font-size: 13px;
                margin-bottom: 8px;
              }
              
              .bill-item {
                padding: 6px 0;
              }
              
              .bill-number,
              .bill-amount {
                font-size: 11px;
              }
              
              .receipt-footer {
                padding: 12px 20px;
              }
              
              .footer-text {
                font-size: 10px;
                line-height: 1.4;
              }
              
              .transaction-id {
                padding: 4px 8px;
                font-size: 9px;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="receipt-header">
              <div class="jawwal-logo">
                <img src="https://cdnjobs.net/cached_uploads/full/2020/02/16/jawwal-1581878215.png" alt="Jawwal Logo" />
              </div>
              <div class="receipt-title">Payment Receipt</div>
              <div class="receipt-subtitle">Jawwal Bill Payment</div>
            </div>
            
            <div class="receipt-body">
              <div class="status-badge">✓ ${transactionData?.status || transactionData?.reason || 'Success'}</div>
              
              <div class="info-grid">
                <div class="info-item">
                  <span class="info-label">Phone Number</span>
                  <span class="info-value">${number}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Transaction ID</span>
                  <span class="info-value transaction-id">${transactionData?.trans_id || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Reference Number</span>
                  <span class="info-value">${transactionData?.jawwal_reference || transactionData?.data?.jawwal_reference || transactionData?.reference || transactionData?.data?.reference || transactionData?.ref || transactionData?.data?.ref || transactionData?.reference_number || transactionData?.data?.reference_number || transactionData?.data?.bill_reference || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Payment Date</span>
                  <span class="info-value">${formattedDate}</span>
                </div>
              </div>
              
              <div class="decorative-line"></div>
              
              <div class="amount-highlight">
                <div class="amount-label">Total Amount Paid</div>
                <div class="amount-value">${actualTotalAmount} NIS</div>
              </div>
              
              ${billsToDisplay.length > 0 ? `
              <div class="bill-details">
                <div class="bill-details-title">Bill Details</div>
                ${billsToDisplay.map(bill => {
      const billAmount = typeof bill.amount === 'number'
        ? bill.amount
        : parseFloat(bill.amount) || 0;
      return `
                    <div class="bill-item">
                      <span class="bill-number">Bill ${bill.bill_number || bill.number || bill.id}</span>
                      <span class="bill-amount">${billAmount} NIS</span>
                    </div>
                  `;
    }).join('')}
              </div>
              ` : ''}
            </div>
            
            <div class="receipt-footer">
              <div class="footer-text">
                Thank you for using Jawwal services<br>
                This receipt serves as proof of payment<br>
                <strong>Printed on: ${formattedDate}</strong>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const createShareMessage = (transactionData, number) => {
    const billDetails = savedBillData.bills.map(bill =>
      `Bill ${bill.bill_number || bill.number}: ${bill.amount} NIS`
    ).join('\n');

    const actualTotalAmount = savedBillData.amount > 0
      ? savedBillData.amount.toFixed(2)
      : '0.00';

    return `Jawwal Bill Payment Receipt:
    
Status: ${transactionData?.status || 'N/A'}
Reason: ${transactionData?.reason || 'N/A'}
Transaction ID: ${transactionData?.trans_id || 'N/A'}
Phone Number: ${number}
Amount Paid: ${actualTotalAmount} NIS
Reference Number: ${transactionData?.jawwal_reference || transactionData?.data?.jawwal_reference || transactionData?.reference || transactionData?.data?.reference || transactionData?.ref || transactionData?.data?.ref || transactionData?.reference_number || transactionData?.data?.reference_number || transactionData?.data?.bill_reference || 'N/A'}

Bill Details:
${billDetails}

Date: ${new Date().toLocaleString()}`;
  };

  const { mutateAsync: payBill, isLoading } = useMutation(apiCall, {
    onSuccess: (res) => {
      console.log('=== BILL PAYMENT SUCCESS ===');
      console.log('Provider:', provider);
      console.log('Response:', res);
      console.log('Response keys:', res ? Object.keys(res) : 'null');
      console.log('Response data:', res?.data);
      console.log('=== END DEBUG ===');

      setIsOpen(false);

      // Handle failure response
      if (res?.status === "failed") {
        const errorReason = res?.reason || "Something went wrong";
        setFailureMessage(errorReason);
        setShowFailureModal(true);
        // State cleanup happens in handleFailureModalClose
        return;
      }

      // Handle no balance/disabled seller
      if (res?.reason === "seller no balamce/ disabled") {
        setFailureMessage("Seller account has no balance or is disabled");
        setShowFailureModal(true);
        // State cleanup happens in handleFailureModalClose
        return;
      }

      // Save bill data BEFORE clearing selections
      setSavedBillData({
        bills: Array.isArray(selectedBill) ? [...selectedBill] : [selectedBill],
        amount: parseFloat(changeText) || totalAmount || 0
      });

      // Show success modal for successful payment
      setSuccessMessage("Payment processed successfully");
      setTransactionData(res);
      setShowSuccessModal(true);
      setWNumber(null);
      setEmail(null);
      // Clear selections AFTER showing modal to prevent rendering errors
      setTimeout(() => {
        clearSelections();
      }, 100);
      onSubmitSuccess && onSubmitSuccess(res);
    },
    onError: (error) => {
      console.error('=== BILL PAYMENT ERROR ===');
      console.error('Error:', error);
      console.error('=== END ERROR ===');

      setIsOpen(false);
      const errorMsg = error?.response?.data?.reason || error?.message || "Something went wrong!";
      setFailureMessage(errorMsg);
      setShowFailureModal(true);
      // State cleanup happens in handleFailureModalClose
    },
  });

  const handleSaveClick = () => {
    setIsEditable(false);
    if (changeText.trim()?.length === 0) {
      setChangeText(totalAmount);
    }
  };

  const handleCancelClick = () => {
    setIsEditable(false);
    setChangeText(totalAmount);
  };

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const onSubmit = async (e) => {
    e.preventDefault();
    const whatsappNumber = `${countryCode}${wNumber}`;

    // For Jawwal with multiple bills, send each bill separately
    if (provider === "jawwal" && selectedBill.length > 1) {
      setIsMultiBillLoading(true);
      let allResults = [];
      let successCount = 0;
      let failureCount = 0;
      let lastSuccessfulResult = null;

      for (let i = 0; i < selectedBill.length; i++) {
        const bill = selectedBill[i];

        const payload = {
          number,
          amount: parseFloat(bill.amount),
          wNumber: wNumber ? whatsappNumber : "N/A",
          email: email ?? "N/A",
          id: bill.id,
          billDetails: [bill],
          identity: identity || bill?.identity || "N/A",
          provider: provider,
          totalBillsCount: 1,
          billNumber: i + 1,
          totalBills: selectedBill.length,
          paymentMethod: "online",
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        };

        // Add any extra fields
        for (const field of extraFields) {
          payload[field.name] = field.value;
        }

        try {
          const result = await apiCall(payload);

          if (result?.status === "failed") {
            allResults.push({ bill, result, success: false, error: result?.reason });
            failureCount++;
          } else {
            allResults.push({ bill, result, success: true });
            lastSuccessfulResult = result;
            successCount++;
          }

          // Wait 2 seconds before sending next bill (except for the last one)
          if (i < selectedBill.length - 1) {
            await sleep(2000);
          }
        } catch (error) {
          allResults.push({ bill, error, success: false });
          failureCount++;
          // Continue to next bill instead of breaking
        }
      }

      // Handle results after all bills processed
      setIsOpen(false);
      setIsMultiBillLoading(false);

      console.log('=== JAWWAL MULTI-BILL RESULTS ===');
      console.log(`Success: ${successCount}, Failed: ${failureCount}`);
      console.log('All results:', allResults);
      console.log('=== END DEBUG ===');

      // Calculate total from successful bills only
      const successfulBills = allResults.filter(r => r.success).map(r => r.bill);
      const calculatedTotal = successfulBills.reduce((sum, bill) => {
        const billAmount = parseFloat(bill.amount) || 0;
        return sum + billAmount;
      }, 0);

      if (successCount === 0) {
        // All bills failed
        const firstError = allResults.find(r => !r.success);
        setFailureMessage(firstError?.result?.reason || firstError?.error?.message || "All bills failed to process");
        setShowFailureModal(true);
        clearSelections();
        return;
      }

      if (failureCount > 0) {
        // Some succeeded, some failed - show partial success
        const failedBills = allResults.filter(r => !r.success);
        const failedBillNumbers = failedBills.map(r => r.bill.bill_number || r.bill.number || r.bill.id).join(', ');

        setSavedBillData({
          bills: successfulBills,
          amount: calculatedTotal
        });
        setSuccessMessage(
          `✅ ${successCount} bill(s) paid successfully\n⚠️ ${failureCount} bill(s) failed\n\nFailed bills: ${failedBillNumbers}`
        );
        setTransactionData(lastSuccessfulResult);
        setShowSuccessModal(true);
        setWNumber(null);
        setEmail(null);
        clearSelections();
        onSubmitSuccess && onSubmitSuccess(lastSuccessfulResult);
        return;
      }

      // All bills succeeded
      setSavedBillData({
        bills: successfulBills,
        amount: calculatedTotal
      });
      setSuccessMessage(`✅ Payment processed successfully for ${successCount} bills`);
      setTransactionData(lastSuccessfulResult);
      setShowSuccessModal(true);
      setWNumber(null);
      setEmail(null);
      clearSelections();
      onSubmitSuccess && onSubmitSuccess(lastSuccessfulResult);
      return;
    }

    const IDs = selectedBill.map((element) => element?.id).join(",");

    // Create payload based on provider type
    let payload;

    if (provider === "mada") {
      // Mada-specific payload format
      const firstBill = selectedBill[0];

      const serviceNumber = firstBill?.originalBill?.raw_bill_data?.user_bill?.serviceIdentifier?.value ||
        firstBill?.originalBill?.raw_bill_data?.userBill?.serviceIdentifier?.value ||
        number;

      const invoiceNumber = firstBill?.originalBill?.invoice_number ||
        firstBill?.originalBill?.biller_reference_id ||
        firstBill?.ref;

      const billingPeriod = firstBill?.originalBill?.cycle ||
        firstBill?.cycle ||
        firstBill?.originalBill?.raw_bill_data?.yearMonth ||
        "N/A";

      payload = {
        service_number: serviceNumber,
        invoice_number: invoiceNumber,
        amount: changeText,
        billing_period: billingPeriod,
        wNumber: wNumber ? whatsappNumber : "N/A",
        email: email ?? "N/A",
        billDetails: selectedBill, // Send complete bill details for reference
        provider: provider,
        totalBillsCount: selectedBill.length,
        paymentMethod: "online",
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };
    } else {
      // Generic payload for other providers
      const firstBill = selectedBill?.[0];
      // Toppinex /api/ooredoo/pay-bills expects `period` in YYYY-MM form
      // (e.g. "2026-04"). The bill carries that as `cycle`. The previous
      // `date`/`date1` fallback (e.g. "Apr 26") caused Toppinex to fail with
      // InvalidXMLSchema on the BillingNo element when it built the SOAP
      // request to Ooredoo.
      const resolvedPeriod = (provider?.toLowerCase() === "ooredoo")
        ? (firstBill?.cycle ||
           firstBill?.originalBill?.cycle ||
           firstBill?.date ||
           firstBill?.date1 ||
           IDs)
        : IDs;

      payload = {
        number,
        amount: changeText,
        wNumber: wNumber ? whatsappNumber : "N/A",
        email: email ?? "N/A",
        id: IDs,
        period: resolvedPeriod,
        billDetails: selectedBill, // Send complete bill details
        identity: identity || selectedBill[0]?.identity || "N/A", // Include identity if available
        provider: provider, // Use passed provider
        totalBillsCount: selectedBill.length,
        paymentMethod: "online",
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };
    }

    // Add any extra fields
    for (const field of extraFields) {
      payload[field.name] = field.value;
    }

    await payBill(payload);
  };

  return (
    <div id="general-pay-modal-div">
      <Button
        onClick={() => {
          !isDisabled && setIsOpen(true);
        }}
        id="general-pay-modal-pay-btn"
        disabled={isDisabled || isMultiBillLoading}
        variant="contained"
        startIcon={isMultiBillLoading ? <CircularProgress size={20} color="inherit" /> : null}
      >
        {isMultiBillLoading ? "Processing..." : title}
      </Button>
      {isOpen && (
        <Modal
          visible={isOpen}
          onClose={() => setIsOpen(false)}
          title={title}
          width={"40vw"}
          id="general-pay-modal"
          onSuccess={onSubmit}
          onSuccessLoading={isLoading || isMultiBillLoading}
          onSuccessDisable={isEditable}
          titleButton={(isLoading || isMultiBillLoading) ? "We are processing your bill" : "submit"}
        >
          <form className="phone-form" id="general-pay-modal-form">
            <div className="d-flex w-100 justify-content-around mb-4">
              <div
                className="bg-light p-3 rounded"
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <h6 className="m-0">
                  {translate("Number")}: {number}
                </h6>
              </div>
              <div className="bg-light p-3 rounded">
                {!isEditable ? (
                  <div
                    className="bg-light p-3 rounded d-flex  "
                    style={{
                      alignItems: "center",
                    }}
                  >
                    <h6 className="m-0">
                      {translate("money")}: {changeText} ILS
                    </h6>
                    {showEditAmount && (
                      <IconButton
                        onClick={() => setIsEditable(true)}
                        size="small"
                      >
                        <EditIcon color="success" /> {translate("Edit")}
                      </IconButton>
                    )}
                  </div>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span> {translate("money")}: </span>
                    <TextField
                      value={changeText}
                      onChange={(e) => setChangeText(e.target.value)}
                      variant="outlined"
                      size="small"
                      style={{ width: 100 }}
                    />
                    <IconButton onClick={handleSaveClick} size="small">
                      <SaveIcon sx={{ ml: "2px", mr: "2px" }} color="info" />
                    </IconButton>
                    <IconButton onClick={handleCancelClick} size="small">
                      <CancelIcon sx={{ ml: "2px", mr: "2px" }} color="error" />
                    </IconButton>
                  </Box>
                )}
              </div>
            </div>
            <Box sx={{ display: "flex", width: "80%" }}>
              <Select
                value={countryCode}
                onChange={handleCountryCodeChange}
                sx={{ width: "25%" }}
              >
                <MenuItem value="972">+972 </MenuItem>
                <MenuItem value="970">+970 </MenuItem>
                {/* Add more country codes and names as needed */}
              </Select>
              <TextField
                value={wNumber}
                type="number"
                onChange={(e) => setWNumber(e.target.value)}
                label={translate("Whatsapp number")}
                id="phone-number-input-field"
                sx={{ width: "75%" }}
              />
            </Box>
            <Box sx={{ width: "80%", mt: "16px" }}>
              <TextField
                value={email}
                type="email"
                id="general-pay-modal-email-input-field"
                onChange={(e) => setEmail(e.target.value)}
                label="Email"
                sx={{ width: "100%" }}
              />
            </Box>
            {extraFields.map((field, index) => (
              <Box sx={{ width: "80%", mt: "16px" }} key={index}>
                <TextField
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  label={field.label}
                  type={field.type}
                  sx={{ width: "100%" }}
                />
              </Box>
            ))}
          </form>
        </Modal>
      )}
      <TransactionFailureModal
        open={showFailureModal}
        onClose={handleFailureModalClose}
        title={translate("Transaction Failed")}
        message={failureMessage}
      />
      <TransactionSuccessModal
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        title={translate("Transaction Successful")}
        message={successMessage}
        showShareButton={transactionData !== null}
        showPrintButton={transactionData !== null}
        onShareClick={handleShareClick}
        onPrintClick={handlePrintClick}
      />
      <ShareModal
        shareModal={showShareModal}
        setShareModal={setShowShareModal}
        contentMessage={transactionData ? createShareMessage(transactionData, number) : ''}
      />

      {/* Print Preview Modal */}
      {showPrintPreview && (
        <Modal
          visible={showPrintPreview}
          onClose={() => setShowPrintPreview(false)}
          title="Print Preview"
          width="90vw"
          height="90vh"
          id="print-preview-modal"
        >
          <div style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            backgroundColor: '#f5f5f5'
          }}>
            <div
              dangerouslySetInnerHTML={{ __html: createModernReceiptHTML() }}
              style={{
                transform: 'scale(0.8)',
                transformOrigin: 'top center',
                maxHeight: '100%',
                overflow: 'auto'
              }}
            />
            <div style={{
              display: 'flex',
              gap: '15px',
              marginTop: '20px',
              justifyContent: 'center'
            }}>
              <Button
                onClick={() => setShowPrintPreview(false)}
                variant="outlined"
                color="secondary"
                sx={{ minWidth: 120 }}
              >
                {translate("Cancel")}
              </Button>
              <Button
                onClick={handleConfirmPrint}
                variant="contained"
                color="primary"
                sx={{ minWidth: 120 }}
              >
                {translate("Print")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default GeneralPayModal;
